import useUpdateCustomerContact from '@/api/mutations/customers/useUpdateCustomerContacts';
import { type Contact } from './contacts';
import { type DraftSendEmailPreviewValues } from './DraftSendEmailPreviewFieldset';
import { type DraftSendEmailState } from './types';

/**
 * Resend submission for the draft-send email form. In `bounced` state the
 * recipient email is edited inline; when it differs from the Contact's
 * stored email, the Contact is updated first and the resend only proceeds
 * on success. The caller keeps its own resend mutation via `onResend`.
 */
export const useBouncedEmailCorrection = (params: {
  state: DraftSendEmailState;
  recipient: Contact | null;
  onResend: (values: DraftSendEmailPreviewValues) => void;
}): {
  submitResend: (values: DraftSendEmailPreviewValues) => void;
  isPending: boolean;
} => {
  const { state, recipient, onResend } = params;
  const updateCustomerContact = useUpdateCustomerContact();

  const submitResend = (values: DraftSendEmailPreviewValues) => {
    if (!recipient) return;
    if (state === 'bounced') {
      const trimmedEmail = values.recipientEmail.trim();
      if (trimmedEmail !== (recipient.email ?? '')) {
        updateCustomerContact.mutate(
          { customerContactId: recipient.publicId, email: trimmedEmail },
          { onSuccess: () => onResend(values) }
        );
        return;
      }
    }
    onResend(values);
  };

  return { submitResend, isPending: updateCustomerContact.isPending };
};
